(ns dre.widgets.question-answering.views
  (:require
   ["antd" :refer [Spin Divider Popover Menu]]
   [applied-science.js-interop :as j]
   [clj-commons.humanize :as h]
   [cljs.reader :as edn]
   [clojure.string :as str]
   [clojure.set :refer [intersection]]
   [dre.app.events :as app]
   [dre.app.router :as router :refer [->route]]
   [dre.config :as conf]
   [dre.i18n.events :as-alias i18n]
   [dre.i18n.views :refer [tr]]
   [dre.components.icon :refer [fa-icon icon]]
   [dre.components.inputs :refer [input-search]]
   [dre.components.search-results.views.article :refer [content-no-abstract]]
   [dre.components.select :refer [select]]
   [dre.components.table :refer [table]]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.components.widget.views :as widget-base]
   [dre.components.widget.views.common :refer [no-result title-with-filter]]
   [dre.modals.events :as-alias modals]
   [dre.page-util :refer [nbsp dangerous]]
   [dre.utils :as utils]
   [dre.utils.bem :as bem]
   [dre.widgets.question-answering.events :as events]
   [markdown.core :as md]
   [reagent.core :as r]
   [reagent.dom :as dom]
   [re-frame.core :as rf]
   [reagent.format :refer [format]]))

(def class-name
  :qa-widget)

(defn- extract-articles-table-loading-data
  [articles-table-data]
  (set (remove nil? (mapcat (fn [[k v]]
                              (map (fn [[kk vv]] (when (= vv :loading)
                                                   [k kk])) (:state v))) articles-table-data))))

(defn- replace-newlines
  [str-vec]
  (mapv #(if (= % "\n")
           "<br/>"
           %)
        str-vec))

(defn- index-of-seq
  [str last-occur substr replace-val res]
  (let [start-idx (str/index-of str substr last-occur)
        end-idx (+ start-idx (count substr))]
    (if start-idx
      (recur str end-idx substr replace-val (concat res [[start-idx end-idx replace-val substr]]))
      res)))

(defn- start
  [reference guid]
  (format "<span class='question-answering-answer' data-reference='%s' data-guid='%s'>" reference guid))

(defn- end
  []
  "</span>")

(defn- element-with-article-detail
  [element article]
  (let [tooltip [:div
                 [:h3 (:article/title article)]
                 [:br]
                 [:p (:article/abstract article)]]]
    [:> Popover
     {:content (r/as-element tooltip)
      :trigger :hover
      :mouseEnterDelay 1
      :rootClassName :ant-theme
      :placement :top
      :overlayStyle {:max-width "50vw"}
      :overlayInnerStyle {:max-height "35vh"
                          :overflow-y "auto"}}
     element]))

(defn- wrap-references
  [element guid-ta highlights highlights?]
  (doseq [el (seq (.getElementsByClassName element "question-answering-answer"))]
    (let [reference (edn/read-string (j/get-in el [:dataset :reference]))
          guid (edn/read-string (j/get-in el [:dataset :guid]))
          ta (guid-ta (name guid))
          highlights-by-guid (group-by :article/guid highlights)
          selected-highlights (get highlights-by-guid (name guid))
          abstract-highlights (filter #(= (:section %) "article/abstract") selected-highlights)
          section-highlights (filter #(not= (:section %) "article/abstract") selected-highlights)
          abstract (or (:abstract ta) "")
          spans (remove nil? (map (fn [x]
                                    (let [h (str/trim (:text x))
                                          idx (str/index-of (str/lower-case abstract) (str/replace (str/lower-case h) #"[!.?]*(?:\s+)?$(?<=)" ""))]
                                      (if idx
                                        [idx (+ idx (count h)) true]
                                        nil)))
                                  abstract-highlights))
          spans (sort-by first spans)
          spans (reduce (fn [res span]
                          (cond
                            (and (not (seq res)) (< 0 (first span)))
                            (concat [[0 (dec (first span)) false]] res [span])
                            (not (seq res))
                            (concat res [span])
                            (>= (second (last res)) (second span))
                            res
                            (>= (second (last res)) (first span))
                            (concat res [[(inc (second (last res))) (second span) true]])
                            (<= (- (first span) (second (last res))) 1)
                            (concat res [span])
                            :else
                            (concat res [[(inc (second (last res))) (dec (first span)) false]] [span])))
                        [] spans)
          spans (cond
                  (not (seq spans))
                  (concat spans [[0 (count abstract) false]])
                  (< (second (last spans)) (count abstract))
                  (concat spans [[(inc (second (last spans))) (count abstract) false]])
                  :else
                  spans)
          tooltip
          [:> Spin {:tip "Loading highlights" :spinning (not highlights?)}
           [:div
            [:h3 (:title ta)]
            [:br]
            [:p (doall
                 (map (fn [span]
                        (let [bold? (last span)
                              s (first span)
                              e (second span)]
                          (if bold?
                            ^{:key (str "qa-" s "-" e)}
                            [:span [:b (subs abstract s e)] " "]
                            ^{:key (str "qa-" s "-" e)}
                            [:span (subs abstract s e) " "])))
                      spans))]
            (when (seq section-highlights)
              [:<>
               [:> Divider
                [:b [tr "Claims sourced from the publication full text (by sections)"]]]
               (doall
                (map (fn [[k v]]
                       ^{:key (str "highlight-section-" k)}
                       [:<>
                        [:h3 (str k)]
                        [:p [:ul (doall
                                  (map (fn [t]
                                         ^{:key (str "highlight-section-text-" t)}
                                         [:li  [:b t]]) (map :text v)))]]])
                     (group-by :section section-highlights)))])]]]
      (dom/render [:> Popover
                   {:content (r/as-element tooltip)
                    :trigger :hover
                    :rootClassName :ant-theme
                    :placement :top
                    :overlayStyle {:max-width "50vw"}
                    :overlayInnerStyle {:max-height "35vh"
                                        :overflow-y "auto"}}
                   [:a
                    {:class (bem/be class-name :answer-reference)
                     :target "_blank"
                     :href (->route :detail {:id (name guid)})}
                    reference]] el))))

(defn answer-with-references
  [answer-str highlights highlights? guid-idx guid-ta]
  (let [element (r/atom nil)]
    (r/create-class
     {:display-name
      "annotated-answer"

      :component-did-mount
      #(wrap-references @element guid-ta highlights highlights?)

      :component-did-update
      (fn [this _]
        (let [[_ new-answer-str new-highlights new-highlights?] (r/argv this)]
          (wrap-references @element guid-ta new-highlights new-highlights?)))

      :component-will-unmount
      #(doseq [el (seq (.getElementsByClassName @element "question-answering-answer"))]
         (dom/unmount-component-at-node el))

      :reagent-render
      (fn [answer-str highlights highlights? guid-idx guid-ta]
        (let [guids (keys guid-idx)
              spans (apply concat (remove nil? (map (fn [guid]
                                                      (let [v (index-of-seq answer-str 0 guid (guid-idx guid) [])]
                                                        (when (seq v)
                                                          v))) guids)))
              spans (reverse (sort-by first spans))
              annotated (atom (replace-newlines (vec answer-str)))]
          (doseq [[a b c d] spans]
            (swap! annotated assoc a (str (start c d) (@annotated a)))
            (swap! annotated assoc (dec b) (str (@annotated (dec b)) (end))))
          (dangerous :span {:ref #(reset! element %)} (md/md->html (apply str @annotated)))))})))

(defn- controls-in-pubmed
  []
  [:span
   {:class (bem/be class-name :control-item)
    :on-click #(rf/dispatch [::events/toggle-pubmed-filter])}
   [:input {:type "checkbox"
            :on-change identity
            :checked @(rf/subscribe [::events/in-pubmed-only?])}]
   nbsp
   [:label "In Pubmed only"]
   nbsp])

(defn- controls-in-cs
  []
  [:span
   {:class (bem/be class-name :control-item)
    :on-click #(rf/dispatch [::events/toggle-cs-filter])}
   [:input {:type "checkbox"
            :on-change identity
            :checked @(rf/subscribe [::events/in-cs-only?])}]
   nbsp
   [:label "In Clinical Studies only"]
   nbsp])

(defn- controls-drevidence-model
  []
  [:span
   {:class (bem/be class-name :control-item)
    :on-click #(rf/dispatch [::events/toggle-drevidence-model-filter])}
   [:input {:type "checkbox"
            :on-change identity
            :checked @(rf/subscribe [::events/drevidence-model?])}]
   nbsp
   [:label "Use DrEvidence model"]
   nbsp])

(defn- controls-focus-on-recent-work
  []
  [:span
   {:class (bem/be class-name :control-item)
    :on-click #(rf/dispatch [::events/toggle-focus-on-recent-work])}
   [:input {:type "checkbox"
            :on-change identity
            :checked @(rf/subscribe [::events/focus-on-recent-work?])}]
   nbsp
   [:label "Focus on recent work"]
   [tooltip-wrapper
    (r/as-element [:span [tr "Focus on recent work prioritizes more recently published articles along with the most relevant older articles that answer your search question."]])
    [:span {:style {:padding-left "0.25rem"}} [icon :common :question-mark-circle]]]
   nbsp])

(defn render-article
  [article guid-idx]
  [:div
   {:class (bem/be class-name :item)}
   [:div
    {:class (bem/be class-name :item-index)}
    (guid-idx (:article/guid article)) "."]
   [:div
    {:class (bem/be class-name :item-content)}
    [content-no-abstract {:title (:article/title article)
                          :category (:article/category article)
                          :meta (:article/meta article)
                          :source (:article/source article)
                          :uri (:article/uri article)
                          :persons (update-keys
                                    (:article/persons article)
                                    keyword)
                          :guid  (:article/guid article)
                          :published (:article/published article)}]]])

(defn -clean-answer
  [answer]
  (let [ans (str/trim (cond (and (str/includes? answer "<think>")
                                 (not (str/includes? answer "</think>")))
                            ""
                            (str/includes? answer "</think>")
                            (last (str/split answer #"</think>"))
                            :else answer))
        ans (if (str/starts-with? ans ":")
              (str/trim (str/replace-first ans #":" ""))
              ans)]
    ans))

(defn render-related-articles-table
  [selected-columns articles-not-answer guid-idx-related articles-table-data articles-table-data-loading articles-table-data-loading?]
  (let [max-columns 2]
    [table
     {:title #(r/as-element [:div
                             {:class (bem/be class-name :related-articles-table-header)}
                             [select {:mode "multiple"
                                      :maxCount max-columns
                                      :suffixIcon (r/as-element [:span (count selected-columns) "/" max-columns])
                                      :filterOption false
                                      :value selected-columns
                                      :onChange (fn [e] (rf/dispatch [::events/set-articles-table-selected-columns e]))
                                      :placeholder (r/as-element [:span [tr "Search columns to display"] " (select max " max-columns ")"])
                                      :options events/related-sources-table-columns}]])
      :columns (concat
                [{:title [tr "Paper"]
                  :render (fn [article]
                            [element-with-article-detail
                             [:<>
                              (render-article article guid-idx-related)]
                             article])}
                 {:title [tr "Summary"]
                  :render (fn [article]
                            (let [column-name :summary
                                  table-data (get articles-table-data (keyword (:article/guid article)) {})
                                  column-data (get table-data column-name)]
                              (if column-data
                                [element-with-article-detail
                                 [:div
                                  {:class (bem/be class-name :answer)}
                                  [answer-with-references
                                   (events/clean-answer (:answer column-data))
                                   (get column-data :highlights)
                                   (contains? column-data :highlights)
                                   guid-idx-related
                                   (into {} (map (fn [a] [(:article/guid a) {:title (:article/title article)
                                                                             :abstract (:article/abstract article)}])
                                                 (get column-data :articles)))]]
                                 article]
                                [:> Spin {:spinning (and articles-table-data-loading?
                                                         (contains? articles-table-data-loading [(keyword (:article/guid article)) column-name]))}
                                 [tooltip-wrapper
                                  (r/as-element [tr "Generate summary"])
                                  [:button
                                   {:class (bem/bm :button (concat [:brand :medium] (when articles-table-data-loading?
                                                                                      [:disabled])))
                                    :on-click #(rf/dispatch [::events/fetch-selected (:article/guid article) (:article/uri article) (:article/source article) column-name nil])}
                                   [fa-icon :pencil]]]])))}]
                (map (fn [column-name]
                       (let [meta (get events/related-sources-table-columns-index column-name)]
                         {:title (:label meta)
                          :render (fn [article]
                                    (let [table-data (get articles-table-data (keyword (:article/guid article)) {})
                                          column-data (get table-data column-name)]
                                      (if column-data
                                        [element-with-article-detail
                                         [:div
                                          {:class (bem/be class-name :answer)}
                                          [answer-with-references
                                           (events/clean-answer (:answer column-data))
                                           (get column-data :highlights)
                                           (contains? column-data :highlights)
                                           guid-idx-related
                                           (into {} (map (fn [a] [(:article/guid a) {:title (:article/title article)
                                                                                     :abstract (:article/abstract article)}])
                                                         (get column-data :articles)))]]
                                         article]
                                        [:> Spin {:spinning (and articles-table-data-loading?
                                                                 (contains? articles-table-data-loading [(keyword (:article/guid article)) column-name]))}
                                         [tooltip-wrapper
                                          (r/as-element [tr "Extract data"])
                                          [:button
                                           {:class (bem/bm :button (concat [:brand :medium] (when articles-table-data-loading?
                                                                                              [:disabled])))
                                            :on-click #(rf/dispatch [::events/fetch-selected (:article/guid article) (:article/uri article) (:article/source article) column-name (:query meta)])}
                                           [fa-icon :pencil]]]])))})) selected-columns))
      :dataSource articles-not-answer}]))

(defn widget-content
  [state data question show-related-sources? finished? sources]
  [:div {:class class-name}
   [:div
    {:class (bem/be class-name :input-search)}
    [input-search {:placeholder @(rf/subscribe [::i18n/translate "Ask anything..."])
                   :value question
                   :on-change (fn [e]
                                (rf/dispatch [::events/set-question (-> e .-target .-value)]))
                   :on-search (fn [e]
                                (rf/dispatch [::events/fetch]))}]]
   (if (not (seq data))
     [no-result true]
     (let [answer (or (get-in data [:answer]) "")
           answer (events/clean-answer answer)
           highlights (or (get-in data [:highlights]) [])
           highlights? (and (contains? data :highlights)
                            (nil? (:highlight-stream-id state)))
           answer-sources (first (re-find #"SOURCES:\n(- .*\n?)*$" answer))
           answer-without-sources (str/replace answer (or answer-sources "") "")
           articles-with-index (map
                                (fn [a]
                                  (assoc a :answer-index (str/index-of answer (:article/guid a))))
                                (get-in data [:articles/to-show]))

           articles-in-answer (remove
                               (fn [a]
                                 (nil? (:answer-index a)))
                               articles-with-index)
           articles-in-answer (sort-by :answer-index articles-in-answer)
           all-abstract-in-answer-without-sources? (every? (fn [a]
                                                             (if (str/index-of answer-without-sources (:article/guid a))
                                                               true
                                                               false)) articles-in-answer)
           answer (if all-abstract-in-answer-without-sources?
                    answer-without-sources
                    answer)
           articles-not-answer (filter
                                (fn [a]
                                  (nil? (:answer-index a)))
                                articles-with-index)
           guid-idx (merge
                     (into {} (map-indexed (fn [idx article] [(:article/guid article) (inc idx)]) articles-in-answer))
                     (into {} (map-indexed
                               (fn [idx article] [(:article/guid article) (+ (inc idx) (count articles-in-answer))])
                               articles-not-answer)))
           guid-ta (into {} (map (fn [article] [(:article/guid article) {:title (:article/title article)
                                                                         :abstract (:article/abstract article)}])
                                 (get-in data [:articles/to-show])))
           articles-table-data (or (get-in data [:articles-table-data]) {})
           articles-table-data-loading (extract-articles-table-loading-data articles-table-data)
           articles-table-data-loading? (seq articles-table-data-loading)
           articles-table-selected-columns (get state :articles-table-selected-columns [])]
       [:div {:class (bem/be class-name :content)}
        [:> Divider
         [:span
          [:b [tr "Summary"]]
          [:span " (" [tr "analyzed"] " " (h/intcomma (:analyzed-articles-count data))
           [tooltip-wrapper
            (r/as-element [:span [tr "Analyzed"] " " (h/intcomma (:analyzed-articles-count data)) " " sources [tr "articles"]])
            [:a "..."]] ")"]]]
        (if (get-in data [:answer])
          [:div
           {:class (bem/be class-name :answer)}
           [answer-with-references answer highlights highlights? guid-idx guid-ta]]
          [no-result true])
        (cond
          (:vote-loading? state)
          [:div
           {:class (bem/be class-name :vote)}
           [:> Spin]]
          (and (:voted? state) (:vote-exist? state))
          nil
          (:voted? state)
          [:div
           {:class (bem/be class-name :vote)}
           [:b
            [tr "Thanks for your feedback!"]]]
          (and finished?
               (not (:voted? state)))
          [:div
           {:class (bem/be class-name :vote)}
           [:b
            [tr "Was this response helpful?"]
            [:span
             {:class (bem/be class-name :thumb)
              :on-click #(rf/dispatch [::events/vote true {}])}
             [fa-icon :thumbs-up]]
            [:span
             {:class (bem/be class-name :thumb)
              :on-click (fn [e]
                          (.stopPropagation e)
                          (rf/dispatch [::modals/set-modal :question-answering/feedback]))}
             [fa-icon :thumbs-down]]]]
          :else
          nil)
        [:> Divider
         [:b [tr "Sources"]]]
        [:div
         {:class (bem/be class-name :items)}
         (if (or (seq articles-in-answer)
                 (and
                  show-related-sources?
                  (seq articles-not-answer)))
           (if (conf/feature-enabled? :question-answering-single-ie)
             [render-related-articles-table
              articles-table-selected-columns
              (concat articles-in-answer (when show-related-sources?
                                           articles-not-answer))
              guid-idx
              articles-table-data
              articles-table-data-loading
              articles-table-data-loading?]
             (doall
              (map-indexed (fn [idx article]
                             ^{:key (:article/guid article)}
                             [:<>
                              [element-with-article-detail
                               (render-article article guid-idx)
                               article]
                              [:> Divider]])
                           (concat articles-in-answer (when show-related-sources?
                                                        articles-not-answer)))))
           [no-result true])]
        (if (= :loading (:state-snippets state))
          [:div
           {:class (bem/be class-name :explore)}
           [:> Spin]]
          (when (seq (get-in data [:articles/tail]))
            [:a
             {:class (bem/be class-name :explore)
              :on-click #(rf/dispatch [::events/fetch-snippets])}
             (if show-related-sources?
               [tr "Load More"]
               [tr "Explore More Related Sources"])]))]))])

(defn view
  [id title]
  (let [state @(rf/subscribe [::events/state])
        data @(rf/subscribe [::events/data])
        user @(rf/subscribe [::app/user])
        admin-role? (utils/admin-role? user)
        env @(rf/subscribe [::conf/get :env])
        question @(rf/subscribe [::events/question])
        show-related-sources? @(rf/subscribe [::events/show-related-sources?])
        loading? (= :loading (:state state))
        finished? (nil? (:stream-id state))
        sources (cond
                  (and (:in-pubmed-only? state) (:in-cs-only? state))
                  "PubMed Clinical Studies "
                  (:in-pubmed-only? state)
                  "PubMed "
                  :else
                  "")
        loading-tip (str "Analyzing citations...")

        articles-table-data (or (get-in data [:articles-table-data]) {})
        articles-table-data-loading (extract-articles-table-loading-data articles-table-data)
        articles-table-data-loading? (seq articles-table-data-loading)]
    [widget-base/view id [title-with-filter title id] [widget-content state data question show-related-sources? finished? sources]
     {:export-image? true
      :csv-event (if admin-role?
                   [::events/export-dev]
                   [::events/export])
      :csv-event-loading? (or loading? (not finished?) articles-table-data-loading?)
      :tooltip (r/as-element [:span
                              [tr "Results are summarized using Dr.Evidence's proprietary Generative AI model and based on up to 5 relevant citations from all of our sources less patents and RSS feeds. For higher recall, please use our search optimization service. We continuously refine our models and algorithms to maximize accuracy. If you encounter content that does not address your query, please report it to help us improve."]])
      :controls [:> Menu
                 {:mode "horizontal"
                  :style {:flex 1
                          :justifyContent "flex-end"
                          :lineHeight "23px"
                          :border-bottom "none"}
                  :selectedKeys []
                  :items [(when (conf/feature-enabled? :question-answering-in-pubmed)
                            {:key 0
                             :label (r/as-element [controls-in-pubmed])})
                          #_(when @(rf/subscribe [::events/in-pubmed-only?])
                              {:key 1
                               :label (r/as-element [controls-in-cs])})
                          (when (conf/feature-enabled? :question-answering-focus-on-recent-work)
                            {:key 2
                             :label (r/as-element [controls-focus-on-recent-work])})
                          (when (and admin-role? (conf/feature-enabled? :question-answering-model-switch?))
                            {:key 3
                             :label (r/as-element [controls-drevidence-model])})]}]
      :selections [(when (conf/feature-enabled? :question-answering-top-k)
                     {:id :top-k
                      :type :dropdown
                      :default-option (keyword (str events/default-top-k))
                      :on-change #(rf/dispatch [::events/set-top-k (js/parseInt (name %))])
                      :options [{:id :5 :label [tr "Top 5 papers"]}
                                {:id :10 :label [tr "Top 10 papers"]}]})]}
     {:loading? loading?
      :loading-tip loading-tip}]))